import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { createSetupIntent, confirmSubscription } from '../../../domain/use_cases/stripe/createStripeSubscription';
import { Authentication } from '@leedpulse-monorepo/classes';
import { loadStripe, Stripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_LEEDFLOW_STRIPE_PUBLIC_KEY);

interface StripeContextValue {
  /** SetupIntent client_secret for stripe.confirmCardSetup() */
  clientSecret: string | undefined;
  stripePromise: Promise<Stripe | null>;
  loadingStatus: ContextStatus;
  subscriptionStatus: ContextStatus;

  /** Step 1 — Initialize by creating a SetupIntent (collects card securely) */
  initializePayment: (companyId: string, companyApiKey: string, auth: Authentication) => Promise<void>;

  /** Step 2 - After card confirmed, create the actual subscription */
  createSubscription: (params: {
    companyId: string;
    companyApiKey: string;
    auth: Authentication;
    planId: string;
    professionalNumber: string;
    paymentMethodId: string;
    address?: {
      line1: string;
      line2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
    billingName?: string;
  }) => Promise<{ subscription_id: string; status: string }>;
}

const StripeContext = createContext<StripeContextValue>({
  clientSecret: undefined,
  stripePromise: stripePromise,
  loadingStatus: ContextStatus.IDLE,
  subscriptionStatus: ContextStatus.IDLE,
  initializePayment: async () => void 0,
  createSubscription: async () => ({ subscription_id: '', status: '' }),
});

export const StripeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [loadingStatus, setLoadingStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [subscriptionStatus, setSubscriptionStatus] = useState<ContextStatus>(ContextStatus.IDLE);

  /**
   * Step 1 — Create a SetupIntent for the company's Stripe customer.
   * Returns the client_secret so the frontend can call stripe.confirmCardSetup().
   */
  const initializePayment = async (companyId: string, companyApiKey: string, auth: Authentication) => {
    setLoadingStatus(ContextStatus.LOADING);
    try {
      const response = await createSetupIntent({
        company_id: companyId,
        company_api_key: companyApiKey,
      }, auth);
      setClientSecret(response.client_secret);

      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not initialized');

      setLoadingStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error("Error initializing setup intent:", error);
      setLoadingStatus(ContextStatus.ERROR);
    }
  };

  /**
   * Step 2 — After the card is confirmed via SetupIntent, create the subscription
   * with the confirmed payment method. Stripe charges the first invoice immediately.
   */
  const handleCreateSubscription = async (params: {
    companyId: string;
    companyApiKey: string;
    auth: Authentication;
    planId: string;
    professionalNumber: string;
    paymentMethodId: string;
    address?: {
      line1: string;
      line2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
    billingName?: string;
  }) => {
    setSubscriptionStatus(ContextStatus.LOADING);
    try {
      const response = await confirmSubscription({
        company_id: params.companyId,
        company_api_key: params.companyApiKey,
        plan_id: params.planId,
        professional_number: params.professionalNumber,
        payment_method_id: params.paymentMethodId,
        address: params.address,
        billing_name: params.billingName,
      }, params.auth);
      setSubscriptionStatus(ContextStatus.SUCCESS);
      return response;
    } catch (error) {
      console.error("Error creating subscription:", error);
      setSubscriptionStatus(ContextStatus.ERROR);
      throw error;
    }
  };

  return (
    <StripeContext.Provider
      value={{
        clientSecret,
        stripePromise,
        loadingStatus,
        subscriptionStatus,
        initializePayment,
        createSubscription: handleCreateSubscription,
      }}
    >
      {children}
    </StripeContext.Provider>
  );
};

export default StripeContext;
